import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { action, autorun, makeObservable, observable } from 'mobx';

import i18n from '../../i18n/config';
import { getFirebaseAuthToken } from '../../services/firebase';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class GameUiStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    window.addEventListener('message', this.onMessageEvent);

    ldClient?.on('ready', () => {
      this.setNewGameUiAuthEnabled(resolveFlag('game_ui_new_authentication', true));
    });

    ldClient?.on('change', (changes) => {
      if ('game_ui_new_authentication' in changes) {
        this.setNewGameUiAuthEnabled(resolveFlag('game_ui_new_authentication', true));
      }
    });

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        this.setNewGameUiAuthEnabled(resolveFlag('game_ui_new_authentication', true));
      }
    });
  }

  @observable
  public newGameUiAuthEnabled: boolean = false;

  @action
  public setNewGameUiAuthEnabled(flag: boolean) {
    this.newGameUiAuthEnabled = flag;
  }

  public getGameUiURL = (path: string, params: Record<string, string | number | boolean | undefined> = {}) => {
    const allParams = {
      ...params,
      platform: 'WEB',
      lang: i18n.language,
      version: this.newGameUiAuthEnabled ? '3' : '2',
    };
    return `${import.meta.env.VITE_GAME_UI_URL}/${path}?${new URLSearchParams(omitBy(allParams, isUndefined)).toString()}`;
  };

  public openGameUiDialog = (path: string, params: Record<string, string | number | boolean | undefined> = {}) => {
    const { dataTestId } = params;
    const url = this.getGameUiURL(path, params);
    this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenGameUiDialog, options: { url, dataTestId } });
  };

  public closeGameUiDialog = () => {
    this.rootStore.uiStore.setCloseDialog();
  };

  private onMessageEvent = (event: MessageEvent) => {
    const { data, origin, source } = event;

    if (origin !== import.meta.env.VITE_GAME_UI_URL) {
      return;
    }

    switch (data.type) {
      case 'getFlags':
        if (source && ldClient) {
          source.postMessage(
            {
              type: 'getFlags_response',
              payload: { flags: ldClient.allFlags() },
            },
            { targetOrigin: import.meta.env.VITE_GAME_UI_URL },
          );
        }
        break;
      case 'getIdToken': {
        void getFirebaseAuthToken().then((token) => {
          if (source && token) {
            source.postMessage(
              {
                type: 'getIdToken_response',
                payload: { token },
              },
              { targetOrigin: import.meta.env.VITE_GAME_UI_URL },
            );
          }
        });
        break;
      }
      // Crown Rips parallel launch: the iframe (opened before launch-game finished) asks for its
      // session; CrownRipsStore delivers it — or re-runs launch-game — via this iframe window.
      case 'crownRipsSessionRequest':
        this.rootStore.crownRipsStore.handleSessionRequest(source as Window | null);
        break;
    }
  };
}
